import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ComboBoxModule, DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { NgxMaskDirective } from 'ngx-mask';
import { Observable } from 'rxjs';
import { SharedTextInputDialogComponent } from 'src/app/access-control/text-input-dialog/text-input-dialog.component';
import { ListItem } from 'src/app/shared/models/list-item.model';
import { NovaValidators } from 'src/app/shared/validators/nova.validators';
import {
    AccessControlClientTemplate,
    AccessControlContractCompanyTemplate
} from '../../models/access-control-contract-company-template.model';
import { AccessControlContactInformation } from '../../models/access-control-information.model';
import { AccessControlContactInformationEnum } from '../../models/enums/access-control-contact-information.enum';
import { AccessControlCompanyTemplateTypesEnum } from '../../models/enums/access-control-template-types.enum';
import { FeatureFlag } from '../../models/enums/feature-flag';
import { PhonePipe } from '../../pipes/phone.pipe';
import { SanitizerPipe } from '../../pipes/sanitizer.pipe';
import { LDFeatureManager } from '../../services/ld-feature-manager';
import { ToasterService } from '../../services/toaster.service';
import { ControlMasks } from '../../validators/control-masks';
import { RichEditorComponent } from '../rich-editor/rich-editor';
import { SharedAccessControlService } from './access-control-service.component';

@UntilDestroy()
@Component({
    selector: 'nova-access-control-information-view-edit',
    templateUrl: './access-control-information-view-edit.component.html',
    styleUrls: ['./access-control-information-view-edit.component.scss'],
    standalone: true,
    imports: [
        AsyncPipe,
        FormsModule,
        ReactiveFormsModule,
        MatCardModule,
        MatSelectModule,
        ComboBoxModule,
        MatFormFieldModule,
        RichEditorComponent,
        MatIconModule,
        MatButtonModule,
        RouterModule,
        MatCheckboxModule,
        TitleCasePipe,
        PhonePipe,
        SanitizerPipe,
        MatProgressSpinnerModule,
        NgxMaskDirective
    ]
})
export class AccessControlInformationViewEditComponent implements OnInit {
    editMode = false;
    @Input() formType: string;
    @Input() companyInfo: AccessControlContactInformation;
    @Input() candidateId: number;
    @Input() templateTypeOptions: ListItem[];
    @Input() _allowTemplateEdit = false;
    @Input() set loadingInfo(value: boolean) {
        this.isLoading = value;
        if (!value) {
            this.editMode = value;
        }
    }

    @Input() set contractTemplatesList(list: AccessControlContractCompanyTemplate[]) {
        if (list) {
            this.contractTemplates = list;
        }
    }

    @Input() set clientTemplatesList(list: AccessControlClientTemplate[]) {
        if (list) {
            this.clientTemplates = list;
        }
    }

    @Input() set allowTemplateTypeSelection(val: boolean) {
        if (val) {
            this._allowTemplateTypeSelection = val;
        }
    }

    @Input() set allowTemplateEdit(val: boolean) {
        if (val) {
            this._allowTemplateEdit = val;
        }
    }

    @Input() set postValidationTemplateSave(tempNameExists: boolean) {
        if (this.companyInfoForm?.value && tempNameExists === false) {
            const templateName = { templateName: this.companyInfoForm.value.templateName };
            const templateType = this.getAccessControlTemplateType(
                this._allowTemplateTypeSelection ? this.companyInfoForm.value?.templateTypeVal : this.formType
            );
            this.manageTemplateSave(templateName, templateType);
        } else if (this.companyInfoForm?.value && tempNameExists === true) {
            this.isLoading = false;
            this._toasterService.toast('This template name is already taken, please enter a different name.');
        }
    }

    @Output() emitCandidateFormChanges = new EventEmitter<any[]>();
    @Output() emitManageTemplateSave = new EventEmitter<any>();
    @Output() validateTemplateName = new EventEmitter<any>();
    @Output() deleteTemplate = new EventEmitter<any>();
    companyInfoForm: FormGroup;

    hasSpaces$: Observable<boolean>;

    otherInfoEdit = '';
    isLoading = false;
    accessControlInfoEnum = AccessControlContactInformationEnum;
    phoneMask = ControlMasks.phoneAlt;
    contractTemplates: AccessControlContractCompanyTemplate[] = [];
    clientTemplates: AccessControlClientTemplate[] = [];
    _allowTemplateTypeSelection = false;
    accessControlTemplateTypeEnum = AccessControlCompanyTemplateTypesEnum;
    filterSettings: DropDownFilterSettings;
    sharedTextInputDialogRef: MatDialogRef<SharedTextInputDialogComponent>;
    currentTemplateName: string;

    featureFlag = FeatureFlag;

    constructor(
        private readonly _featureManager: LDFeatureManager,
        private readonly _dialog: MatDialog,
        private readonly _toasterService: ToasterService,
        private readonly _router: Router,
        private readonly _sharedAccessControlService: SharedAccessControlService
    ) {}

    ngOnInit(): void {
        this.companyInfoForm = new FormGroup({
            candidateId: new FormControl(),
            companyName: new FormControl(),
            contactName: new FormControl(),
            contactPhone: new FormControl('', [NovaValidators.phoneNumber]),
            contactEmail: new FormControl('', [Validators.email]),
            contractorType: new FormControl(),
            costCenter: new FormControl(),
            complianceApproval: new FormControl(),
            otherInfo: new FormControl(),
            contractTemplate: new FormControl(),
            clientTemplate: new FormControl(),
            updateCandidateCompanies: new FormControl(false),
            insertTemplate: new FormControl(false)
        });
        if (this._allowTemplateTypeSelection) {
            this.enableTemplateManagementToAdd();
        }
        if (this._allowTemplateEdit) {
            this.enableTemplateManagementToEdit();
        }
        if (this.companyInfo) {
            this.prepareForm();
        }

        this.filterSettings = {
            caseSensitive: false,
            operator: 'contains'
        };

        this.hasSpaces$ = this._sharedAccessControlService.handleTemplateNameChanges(
            this.companyInfoForm.get('templateName')
        );
    }

    prepareForm() {
        if (this.companyInfo && this.companyInfoForm) {
            let patchValue = {};
            if (this.companyInfo.candidateId) {
                patchValue = {
                    candidateId: this.companyInfo.candidateId
                };
            }

            patchValue['contactName'] = this.companyInfo.contactName;
            patchValue['contactPhone'] = this.companyInfo.contactPhone;
            patchValue['contactEmail'] = this.companyInfo.contactEmail;
            patchValue['otherInfo'] = this.companyInfo.otherInfo;
            patchValue['updateCandidateCompanies'] = false;
            if (this.formType === this.accessControlInfoEnum.CONTRACT) {
                this.currentTemplateName = this.companyInfo.contractTemplate?.name;
                patchValue['templateName'] = this.companyInfo.contractTemplate?.name;
                patchValue['companyName'] = this.companyInfo.companyName;
                patchValue['contractorType'] = this.companyInfo.contractorType;
                patchValue['contractTemplate'] = this.companyInfo.contractTemplate;
            } else if (this.formType === this.accessControlInfoEnum.CLIENT) {
                this.currentTemplateName = this.companyInfo.clientTemplate?.name;
                patchValue['templateName'] = this.companyInfo.clientTemplate?.name;
                patchValue['costCenter'] = this.companyInfo.costCenter;
                patchValue['clientTemplate'] = this.companyInfo.clientTemplate;
                patchValue['complianceApproval'] = this.companyInfo.complianceApproval;
            }

            this.companyInfoForm.patchValue(patchValue);
            this.otherInfoEdit = this.companyInfo.otherInfo;
        }
    }

    editCompanyInfo() {
        this.editMode = true;
        this.prepareForm();
    }

    otherInfoChanged(newVal: string): void {
        if (this.companyInfo && newVal !== this.companyInfo.otherInfo) {
            this.otherInfoEdit = newVal;
            this.companyInfoForm.markAsDirty();
        }
    }

    submitCompanyInfo(formValue) {
        if (
            this._allowTemplateTypeSelection ||
            (this._allowTemplateEdit &&
                String(formValue.templateName).toUpperCase() !== this.currentTemplateName.toUpperCase())
        ) {
            this.isLoading = true;
            const validationObj = {
                templateName: formValue.templateName,
                templateType: this._allowTemplateTypeSelection
                    ? formValue.templateTypeVal
                    : this.getAccessControlTemplateType(this.formType)
            };
            this.validateTemplateName.emit(validationObj);
        } else if (this._allowTemplateEdit) {
            this.isLoading = true;
            const templateName = { templateName: this.companyInfoForm.value.templateName };
            const templateType = this.getAccessControlTemplateType(this.formType);
            this.manageTemplateSave(templateName, templateType);
        } else {
            const params = {
                candidateId: this.candidateId,
                contactName: formValue.contactName,
                contactPhone: formValue.contactPhone,
                contactEmail: formValue.contactEmail,
                otherInfo: this.otherInfoEdit
            };
            this.saveCandidateFormChanges(params, formValue);
        }
    }

    cancel() {
        this.companyInfoForm?.reset();
        if (this._allowTemplateTypeSelection) {
            this._router.navigate(['/access-control/manage-templates/']);
        } else {
            this.editMode = false;
        }
    }

    contractTemplateSelection(value: any): void {
        this.otherInfoEdit = value.otherInfo;
        const patchValue = {
            companyName: value.companyName,
            contactName: value.contactName,
            contactPhone: value.contactPhone,
            contactEmail: value.contactEmail,
            contractorType: value.contractorType
        };
        this.companyInfoForm.patchValue(patchValue);
    }

    clientTemplateSelection(value: any): void {
        this.otherInfoEdit = value.otherInfo;
        const patchValue = {
            contactName: value.contactName,
            contactPhone: value.contactPhone,
            contactEmail: value.contactEmail,
            costCenter: value.costCenter,
            complianceApproval: value.complianceApproval
        };
        this.companyInfoForm.patchValue(patchValue);
    }

    addNewTemplateName(templateType) {
        this.sharedTextInputDialogRef = this._dialog.open(SharedTextInputDialogComponent, {
            data: {
                cancelText: 'Cancel',
                confirmText: 'Save',
                inputLabel: `Template Name`,
                title: 'Enter Template Name',
                innerClass: 'text-input-dialog',
                accessControlCompanyTemplateTypeId: templateType
            },
            panelClass: ['nova__dialog', 'nova__dialog--md']
        });

        this.sharedTextInputDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                if (this._allowTemplateTypeSelection || this._allowTemplateEdit) {
                    this.manageTemplateSave(result, templateType);
                } else {
                    const formValue = this.companyInfoForm.value;
                    const params = {
                        insertTemplate: true,
                        templateName: result.templateName,
                        candidateId: this.candidateId,
                        contactName: formValue.contactName,
                        contactPhone: formValue.contactPhone,
                        contactEmail: formValue.contactEmail,
                        otherInfo: this.otherInfoEdit
                    };

                    this.saveCandidateFormChanges(params, formValue);
                }
            }
        });
    }

    saveCandidateFormChanges(params, formValue) {
        if (this.formType === this.accessControlInfoEnum.CONTRACT) {
            params['companyName'] = formValue.companyName;
            params['contractorType'] = formValue.contractorType;
            params['accessControlContractCompanyTemplateId'] = formValue.contractTemplate;
        } else if (this.formType === this.accessControlInfoEnum.CLIENT) {
            params['costCenter'] = formValue.costCenter;
            params['costCenterId'] = formValue.costCenterId;
            params['accessControlClientCompanyTemplateId'] = formValue.clientTemplate;
            params['complianceApproval'] = formValue.complianceApproval;
        }

        const formData = [
            {
                formType: this.formType,
                candidateId: this.candidateId,
                params: params
            }
        ];

        if (this.editMode) {
            this.emitCandidateFormChanges.emit(formData);
            this.isLoading = true;
        }
    }

    enableTemplateManagementToAdd() {
        this.editMode = true;
        this.companyInfoForm.addControl('templateName', new FormControl('', [Validators.required]));
        this.companyInfoForm.addControl('templateTypeVal', new FormControl('', [Validators.required]));
    }

    enableTemplateManagementToEdit() {
        this.companyInfoForm.addControl('templateName', new FormControl('', [Validators.required]));
    }

    manageTemplateSave(result, templateType) {
        const dataToSave = {
            name: result.templateName,
            contactName: this.companyInfoForm.value.contactName,
            contactPhone: this.companyInfoForm.value.contactPhone,
            contactEmail: this.companyInfoForm.value.contactEmail,
            accessControlCompanyTemplateTypeId: templateType,
            templateType: this.formType,
            otherInfo: this.otherInfoEdit,
            updateCandidateCompanies: this.companyInfoForm.value.updateCandidateCompanies
        };
        if (this.companyInfoForm.valid) {
            if (this.formType === this.accessControlInfoEnum.CONTRACT) {
                if (this._allowTemplateEdit) {
                    dataToSave['accessControlCompanyTemplateId'] =
                        this.companyInfo.contractTemplate?.accessControlCompanyTemplateId;
                    dataToSave['accessControlContractCompanyTemplateId'] =
                        this.companyInfo.contractTemplate?.accessControlContractCompanyTemplateId;
                }

                dataToSave['companyName'] = this.companyInfoForm.value.companyName;
                dataToSave['contractorType'] = this.companyInfoForm.value.contractorType;
            } else if (this.formType === this.accessControlInfoEnum.CLIENT) {
                if (this._allowTemplateEdit) {
                    dataToSave['accessControlCompanyTemplateId'] =
                        this.companyInfo.clientTemplate?.accessControlCompanyTemplateId;
                    dataToSave['accessControlClientCompanyTemplateId'] =
                        this.companyInfo.clientTemplate?.accessControlClientCompanyTemplateId;
                }
                dataToSave['costCenter'] = this.companyInfoForm.value.costCenter;
                dataToSave['complianceApproval'] = this.companyInfoForm.value.complianceApproval;
            }

            this.emitManageTemplateSave.emit(dataToSave);
            this.companyInfoForm.markAsPristine();
            if (this._allowTemplateTypeSelection || this._allowTemplateEdit) {
                this.editMode = this._allowTemplateEdit ? false : this.editMode;
                this.isLoading = false;
            }
        }
    }

    onTemplateTypeChange(templateType: any) {
        this.formType = templateType.value.toLowerCase();
    }

    public getAccessControlTemplateType(value: string): AccessControlCompanyTemplateTypesEnum {
        switch (value.toLowerCase()) {
            case 'contract':
                return AccessControlCompanyTemplateTypesEnum.CONTRACT;
            case 'client':
                return AccessControlCompanyTemplateTypesEnum.CLIENT;
            default:
                return null;
        }
    }

    delete(): void {
        if (confirm('Confirm deletion?')) {
            const templateId =
                this.formType === this.accessControlInfoEnum.CONTRACT
                    ? this.companyInfo.contractTemplate?.accessControlCompanyTemplateId
                    : this.companyInfo.clientTemplate?.accessControlCompanyTemplateId;

            this.deleteTemplate.emit(templateId);
        }
    }
}
